import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ZoomIn, X } from "lucide-react";
import QRCode from "react-qr-code";
import { SERIES, findSeries, HERO_FALLBACK } from "../lib/seriesData";
import Seo from "../components/Seo";

export default function SeriesDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null);
  const series = findSeries(slug);

  const idx = SERIES.findIndex((s) => s.slug === slug);
  const prev = idx > 0 ? SERIES[idx - 1] : null;
  const next = idx >= 0 && idx < SERIES.length - 1 ? SERIES[idx + 1] : null;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!series) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#2B2B2B]">Series not found</h2>
          <button onClick={() => navigate("/catalogue")} className="mt-6 text-[#FF6A00] underline">Back to catalogue</button>
        </div>
      </div>
    );
  }

  const catalogueUrl = typeof window !== "undefined" ? `${window.location.origin}/series/${slug}` : "";

  const titleCase = series.title.replace(/\bSERIES\b/i, "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const seoJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${titleCase} — KNOVAS`,
    url: `https://knovas.in/series/${slug}`,
    description: `${series.tagline} Explore the KNOVAS ${titleCase} series of premium kitchen and wardrobe hardware.`,
    isPartOf: { "@type": "WebSite", name: "KNOVAS", url: "https://knovas.in/" },
  };

  return (
    <section data-testid="series-detail-page" className="relative bg-[#F5F5F5] min-h-screen">
      <Seo
        title={`${titleCase} Series · KNOVAS Kitchen & Wardrobe Hardware`}
        description={`${series.tagline} Explore the KNOVAS ${titleCase} series — premium, engineered hardware for modular kitchens and wardrobes across India.`}
        path={`/series/${slug}`}
        jsonLd={seoJsonLd}
      />
      {/* Header banner */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-[#FF6A00]" />
            <span className="text-xs tracking-[0.4em] uppercase text-[#FF6A00] font-semibold">
              Series {series.number} · KNOVAS
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <h1
                data-testid="series-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#2B2B2B] leading-[0.9]"
                style={{ fontFamily: "'Anton', sans-serif" }}
              >
                {series.title}
              </h1>
              <p className="mt-3 text-base text-[#4B5563]">{series.tagline}</p>
            </div>
            <div className="flex items-center gap-4">
              <span data-testid="series-page-count" className="text-xs tracking-[0.3em] uppercase text-[#6B7280]">
                {series.pageImages.length} {series.pageImages.length === 1 ? "page" : "pages"}
              </span>
              <div className="bg-white border border-[#E5E7EB] p-2">
                <QRCode
                  data-testid="series-qr"
                  value={catalogueUrl || `https://knovas.in/series/${slug}`}
                  size={64}
                  fgColor="#2B2B2B"
                  bgColor="#FFFFFF"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KNOVAS catalogue pages — one per PDF page */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <div
          data-testid="series-catalogue-pages"
          className="space-y-8"
        >
          {series.pageImages.map((src, i) => (
            <button
              key={src}
              type="button"
              data-testid={`catalogue-page-${i + 1}`}
              onClick={() => setLightbox(src)}
              className="group block w-full bg-white border border-[#E5E7EB] hover:border-[#FF6A00] transition-colors overflow-hidden text-left shadow-sm hover:shadow-md"
            >
              <div className="relative">
                <img
                  src={src}
                  alt={`${series.title} — page ${i + 1}`}
                  className="w-full h-auto block"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = HERO_FALLBACK; }}
                />
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-[#FF6A00] text-white px-3 py-2 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold shadow-md">
                    <ZoomIn size={12} /> Enlarge
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Prev / Next */}
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 pb-16">
        <div className="flex flex-col sm:flex-row items-stretch justify-between gap-4 border-t border-[#E5E7EB] pt-8">
          {prev ? (
            <Link
              to={`/series/${prev.slug}`}
              data-testid="series-nav-prev"
              className="group flex-1 flex items-center gap-4 p-4 border border-[#E5E7EB] hover:border-[#FF6A00] transition-colors bg-white"
            >
              <ArrowLeft size={18} className="text-[#FF6A00] group-hover:-translate-x-1 transition-transform" />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">Previous · {prev.number}</div>
                <div className="text-sm font-bold uppercase text-[#2B2B2B] mt-1">{prev.title}</div>
              </div>
            </Link>
          ) : <div className="flex-1" />}
          {next ? (
            <Link
              to={`/series/${next.slug}`}
              data-testid="series-nav-next"
              className="group flex-1 flex items-center gap-4 p-4 border border-[#E5E7EB] hover:border-[#FF6A00] transition-colors justify-end text-right bg-white"
            >
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">Next · {next.number}</div>
                <div className="text-sm font-bold uppercase text-[#2B2B2B] mt-1">{next.title}</div>
              </div>
              <ArrowRight size={18} className="text-[#FF6A00] group-hover:translate-x-1 transition-transform" />
            </Link>
          ) : <div className="flex-1" />}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="catalogue-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <img src={lightbox} alt="Catalogue page" className="max-h-[92vh] max-w-full object-contain" />
        </div>
      )}
    </section>
  );
}
